"use client";

import {
    createContext,
    Dispatch,
    FC,
    FormEvent,
    SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { AssetsDirectory, DeliveryChannel, TranslationScopes } from "@finbackoffice/enums";
import { ClientBFFContext } from "@finbackoffice/site-core";
import { ConfigContextV2, UserAccountContextV2 } from "contexts";
import AccountHeader from "components/account/header/AccountHeader";
import Button from "components/base/button/Button";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import AccountDetailsFormBlock from "../form-block/AccountDetailsFormBlock";
import styles from "./two-factor.module.sass";
import AuthChannelRadioInput from "./AuthChannelRadioInput";

type IProps = {
    showHeader?: boolean;
};

type ITwoFactorContext = {
    onSubmit: (e: FormEvent<Element>) => Promise<void>;
    disableTwoFactor: () => Promise<void>;
    loading: boolean;
    setChannel: Dispatch<SetStateAction<"" | DeliveryChannel>>;
    channel: "" | DeliveryChannel;
    isValid: boolean;
    showSmsChannel: boolean;
    showEmailChannel: boolean;
};

export const TwoFactorContext = createContext<ITwoFactorContext>(null as any);

const TwoFactor: FC<IProps> = ({ showHeader = true }) => {
    const client = useContext(ClientBFFContext);

    const { updateUser, userData, isEmailVerified, isPhoneVerified } =
        useContext(UserAccountContextV2);
    const { isPhoneVerificationRequired, isEmailVerificationRequired } =
        useContext(ConfigContextV2);

    const [channel, setChannel] = useState<DeliveryChannel | "">(
        userData?.two_factor_auth_enabled ? userData?.two_factor_delivery_channel : "",
    );
    const [loading, setLoading] = useState(false);

    const isValid = Boolean(channel);
    const showSmsChannel =
        (isPhoneVerified && isPhoneVerificationRequired) || !isPhoneVerificationRequired;

    const showEmailChannel =
        (isEmailVerified && isEmailVerificationRequired) || !isEmailVerificationRequired;

    const updateDeliveryChannel = useCallback(
        async (val: DeliveryChannel) => {
            setLoading(true);
            try {
                const response = await client.updateProfile({
                    two_factor_auth_enabled: true,
                    two_factor_delivery_channel: val,
                });

                if (response.two_factor_auth_enabled) {
                    updateUser(response);
                    if (channel !== val) {
                        setChannel(val);
                    }
                    setLoading(false);
                }
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        },
        [channel, client, updateUser],
    );

    const onSubmit = useCallback(
        async (e: FormEvent) => {
            e.preventDefault();
            if (isValid && channel) {
                await updateDeliveryChannel(channel);
            }
        },
        [channel, isValid, updateDeliveryChannel],
    );

    const disableTwoFactor = useCallback(async () => {
        setLoading(true);
        try {
            const response = await client.updateProfile({
                two_factor_auth_enabled: false,
            });

            if (!response.two_factor_auth_enabled) {
                updateUser(response);
                setChannel("");
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    }, [client, updateUser]);

    const renderLabel = useCallback(
        () => (
            <>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-2step.svg`}
                    className="svg-icon"
                />

                <Translate
                    tid="account_details_2fa_heading"
                    namespace={TranslationScopes.UserAccount}
                />
            </>
        ),
        [],
    );

    const handleChannelChange = useCallback(
        async (val: DeliveryChannel) => {
            if (
                userData?.two_factor_auth_enabled &&
                userData?.two_factor_delivery_channel !== val
            ) {
                await disableTwoFactor();
            }

            setChannel(val);
        },
        [
            disableTwoFactor,
            setChannel,
            userData?.two_factor_auth_enabled,
            userData?.two_factor_delivery_channel,
        ],
    );

    const renderForm = useMemo(
        () => (
            <form onSubmit={onSubmit} className={styles.authTypes}>
                <div>
                    {showSmsChannel && (
                        <AuthChannelRadioInput
                            value={DeliveryChannel.Sms}
                            checked={channel === DeliveryChannel.Sms}
                            iconSrc={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-mobile.svg`}
                            active={
                                userData !== null &&
                                userData?.two_factor_auth_enabled &&
                                userData?.two_factor_delivery_channel === DeliveryChannel.Sms
                            }
                            label={
                                <Translate
                                    tid="account_details_2fa_channel_Sms"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            }
                            info={userData?.phone ?? ""}
                            onChange={() => handleChannelChange(DeliveryChannel.Sms)}
                        />
                    )}
                    {showEmailChannel && (
                        <AuthChannelRadioInput
                            value={DeliveryChannel.Email}
                            checked={channel === DeliveryChannel.Email}
                            iconSrc={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-mail.svg`}
                            active={
                                userData !== null &&
                                userData?.two_factor_auth_enabled &&
                                userData.two_factor_delivery_channel === DeliveryChannel.Email
                            }
                            label={
                                <Translate
                                    tid="account_details_2fa_channel_Email"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            }
                            info={userData?.email ?? ""}
                            onChange={() => handleChannelChange(DeliveryChannel.Email)}
                        />
                    )}
                </div>
                {userData?.two_factor_auth_enabled ? (
                    <Button
                        type="button"
                        variant="secondary"
                        icon="iconReset"
                        onClick={disableTwoFactor}
                        disabled={loading}>
                        <Translate
                            tid="account_details_2fa_disable"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </Button>
                ) : (
                    <Button type="submit" variant="primary" disabled={!isValid || loading}>
                        <Translate
                            tid="account_details_2fa_enable"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </Button>
                )}
            </form>
        ),
        [
            channel,
            handleChannelChange,
            isValid,
            loading,
            disableTwoFactor,
            onSubmit,
            showEmailChannel,
            showSmsChannel,
            userData,
        ],
    );

    return (
        <div className={styles.twoStepAuth} data-testid="2fa-container">
            {showHeader && (
                <AccountHeader
                    headingIcon={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-2step.svg`}
                    tId="account_details_2fa_heading"
                    className={styles.compactHeader}
                />
            )}
            <AccountDetailsFormBlock renderHeader={renderLabel} defaultOpened disableCollapse>
                <div className={styles.compact}>
                    <div>{renderForm}</div>
                </div>
            </AccountDetailsFormBlock>
        </div>
    );
};

export default TwoFactor;
